import * as React from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Checkbox, Select, Box, MenuItem,
    FormControl, InputLabel, TextField, FormControlLabel, Slider, Typography, Grid,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
    InputAdornment
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import CloseIcon from '@mui/icons-material/Close'
import { getTranslation } from '../translations.js'

// Helper function
const array_chunks = (array, chunk_size) => Array(Math.ceil(array.length / chunk_size)).fill().map((_, index) => index * chunk_size).map(begin => array.slice(begin, begin + chunk_size))

// Help Dialog
function HelpDialog({ open, onClose, title, content }) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { bgcolor: '#0d1f33', border: '1px solid rgba(144,202,249,0.2)' } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.08)', pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpOutlineIcon sx={{ color: '#90caf9', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">{title}</Typography>
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: 'gray' }}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
                {content.split('\n').map((line, i) => {
                    if (line.trim() === '') return <Box key={i} sx={{ mb: 1 }} />;
                    if (line.startsWith('###')) return <Typography key={i} variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 2, mb: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>{line.replace('###', '').trim()}</Typography>;
                    if (line.startsWith('•')) return <Typography key={i} variant="body2" sx={{ color: '#cdd' , pl: 1.5, mb: 0.5, '&::before': { content: '"▸"', color: '#90caf9', mr: 1 } }}>{line.replace('•', '').trim()}</Typography>;
                    if (line.startsWith('⚠')) return <Box key={i} sx={{ bgcolor: 'rgba(255,152,0,0.1)', border: '1px solid rgba(255,152,0,0.3)', borderRadius: 1, p: 1, mb: 1 }}><Typography variant="body2" sx={{ color: '#ffb86c' }}>{line}</Typography></Box>;
                    return <Typography key={i} variant="body2" sx={{ color: '#aac', mb: 0.5, lineHeight: 1.7 }}>{line}</Typography>;
                })}
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', px: 3, py: 1.5 }}>
                <Button size="small" onClick={onClose} variant="outlined" color="primary">Tamam</Button>
            </DialogActions>
        </Dialog>
    )
}

// Component moved OUTSIDE to prevent remounting issues
const PluginOption = ({ pluginData, parentKey, userPlugins, onChange, t, presetNames }) => {
    const [helpOpen, setHelpOpen] = React.useState(false)
    const [confirmation, setConfirmation] = React.useState(null)

    // Ensure nested object exists
    if (!userPlugins[parentKey]) userPlugins[parentKey] = {};
    if (pluginData.key && userPlugins[parentKey][pluginData.key] === undefined) userPlugins[parentKey][pluginData.key] = pluginData.default;

    const currentValue = pluginData.key ? userPlugins[parentKey][pluginData.key] : undefined;

    // disabled: true → her zaman devre dışı (seçilemez). disabledWhen → başka alan o değerdeyse devre dışı.
    const siblingValues = userPlugins[parentKey] || {}
    const isDisabled = pluginData.disabled === true
        || (pluginData.disabledWhen
            ? siblingValues[pluginData.disabledWhen.key] === pluginData.disabledWhen.value
            : false)

    const handleChange = (newValue) => {
        userPlugins[parentKey][pluginData.key] = newValue;
        onChange({ ...userPlugins });
    }

    // Hint text shown below any field
    const HintText = () => pluginData.hint ? (
        <Typography variant="caption" sx={{ color: '#607080', display: 'block', mt: -0.5, mb: 0.5, pl: 0.5, lineHeight: 1.4 }}>
            {pluginData.hint}
        </Typography>
    ) : null;

    switch (pluginData.type) {
        case "FieldGroup":
            return (
                <Grid container columnSpacing={2} rowSpacing={0} alignItems="center">
                    {pluginData.fields.map((field) => (
                        <Grid item xs={12} md={field.width ?? 6} key={field.key || field.label}>
                            <PluginOption pluginData={field} parentKey={parentKey} userPlugins={userPlugins} onChange={onChange} t={t} presetNames={presetNames} />
                        </Grid>
                    ))}
                </Grid>
            )
        case "Label":
            return (
                <>
                    {pluginData.help && (
                        <HelpDialog
                            open={helpOpen}
                            onClose={() => setHelpOpen(false)}
                            title={pluginData.label.replace(/^[^\w\s]+\s*/, '')}
                            content={pluginData.help}
                        />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', borderBottom: '1px solid #333', pb: 0.5, mb: 1, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: '#90caf9', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                            {t(pluginData.label)}
                        </Typography>
                        {pluginData.help && (
                            <Button
                                size="small"
                                startIcon={<HelpOutlineIcon sx={{ fontSize: 15 }} />}
                                onClick={() => setHelpOpen(true)}
                                sx={{
                                    color: '#f44336', fontSize: '0.78rem', px: 1.2, py: 0.4, minWidth: 0,
                                    border: '1px solid #f44336', borderRadius: 1, textTransform: 'none', fontWeight: 600,
                                    '&:hover': { color: '#fff', borderColor: '#f44336', bgcolor: 'rgba(244,67,54,0.15)' }
                                }}
                            >
                                Yardım
                            </Button>
                        )}
                    </Box>
                </>
            )
        case "Text":
            return (
                <Box sx={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
                    {pluginData.help && <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} title={pluginData.label} content={pluginData.help} />}
                    <TextField
                        fullWidth label={t(pluginData.label)} variant="outlined" size="small"
                        value={currentValue || ""} onChange={(e) => handleChange(e.target.value)}
                        sx={{ my: 1 }} disabled={isDisabled}
                        InputProps={pluginData.help ? {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Button size="small" startIcon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setHelpOpen(true)}
                                        sx={{ color: '#f44336', fontSize: '0.72rem', px: 1, py: 0.3, minWidth: 0,
                                            border: '1px solid #f44336', borderRadius: 1, textTransform: 'none', fontWeight: 600,
                                            '&:hover': { color: '#fff', bgcolor: 'rgba(244,67,54,0.15)' } }}>
                                        Yardım
                                    </Button>
                                </InputAdornment>
                            )
                        } : undefined}
                    />
                    <HintText />
                </Box>
            )
        case "TextArea":
            return (
                <Box sx={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
                    {pluginData.help && <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} title={pluginData.label} content={pluginData.help} />}
                    <TextField fullWidth multiline rows={4} label={t(pluginData.label)} variant="outlined" size="small" value={currentValue || ""} onChange={(e) => handleChange(e.target.value)} sx={{ my: 1, '& .MuiInputBase-root': { fontSize: '0.8rem', fontFamily: 'monospace' } }} disabled={isDisabled} />
                    <HintText />
                </Box>
            )
        case "Checkbox":
            return (
                <Box sx={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
                    {pluginData.help && <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} title={pluginData.label} content={pluginData.help} />}
                    {/* Kritik korumayı kapatma onay dialog'u (window.confirm yerine gerçek popup) */}
                    {pluginData.confirmDisable && (
                        <Dialog open={confirmation === 'disable'} onClose={() => setConfirmation(null)} maxWidth="xs" fullWidth
                            PaperProps={{ sx: { bgcolor: '#0d1f33', border: '1px solid rgba(255,152,0,0.45)' } }}>
                            <DialogTitle sx={{ color: '#ffb86c', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>⚠️ Uyarı</DialogTitle>
                            <DialogContent sx={{ pt: 1 }}><Typography variant="body2" sx={{ color: '#cdd', lineHeight: 1.7 }}>{t(pluginData.confirmDisable)}</Typography></DialogContent>
                            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
                                <Button size="small" onClick={() => setConfirmation(null)} variant="outlined" color="primary">İptal</Button>
                                <Button size="small" onClick={() => { setConfirmation(null); handleChange(false) }} variant="contained" color="error">Eminim, Kapat</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    {pluginData.confirmEnable && (
                        <Dialog open={confirmation === 'enable'} onClose={() => setConfirmation(null)} maxWidth="xs" fullWidth
                            PaperProps={{ sx: { bgcolor: '#0d1f33', border: '1px solid rgba(255,152,0,0.45)' } }}>
                            <DialogTitle sx={{ color: '#ffb86c', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>⚠️ {t(pluginData.confirmEnableTitle || 'Uyarı')}</DialogTitle>
                            <DialogContent sx={{ pt: 1 }}><Typography variant="body2" sx={{ color: '#cdd', lineHeight: 1.7 }}>{t(pluginData.confirmEnable)}</Typography></DialogContent>
                            <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
                                <Button size="small" onClick={() => setConfirmation(null)} variant="outlined" color="primary">İptal</Button>
                                <Button size="small" onClick={() => { setConfirmation(null); handleChange(true) }} variant="contained" color="warning">Kabul Edip Aç</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel control={<Checkbox size="small" checked={!!currentValue} onChange={(_, nv) => {
                            // Kritik koruma kapatılıyorsa (nv=false) → popup aç, onaya kadar değişme.
                            if (nv && pluginData.confirmEnable) { setConfirmation('enable'); return }
                            if (!nv && pluginData.confirmDisable) { setConfirmation('disable'); return }
                            handleChange(nv)
                        }} disabled={isDisabled} />} label={<Typography variant="body2">{t(pluginData.label)}</Typography>} sx={{ my: 0.5, flexGrow: 1 }} />
                        {pluginData.help && (
                            <Button size="small" startIcon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setHelpOpen(true)}
                                sx={{ color: '#f44336', fontSize: '0.72rem', px: 1, py: 0.3, minWidth: 0,
                                    border: '1px solid #f44336', borderRadius: 1, textTransform: 'none', fontWeight: 600, ml: 0.5,
                                    '&:hover': { color: '#fff', bgcolor: 'rgba(244,67,54,0.15)' } }}>
                                Yardım
                            </Button>
                        )}
                    </Box>
                    <HintText />
                </Box>
            )
        case "Select": {
            // dynamicPresets: true → statik "Slot 1..N" yerine gerçek ön ayar isimlerini kullan
            const selection = (pluginData.dynamicPresets && presetNames?.length > 0)
                ? presetNames.map((p, i) => ({
                    value: p.id ?? i,
                    label: p.name ? `Slot ${i + 1} — ${p.name}` : `Slot ${i + 1}`
                  }))
                : pluginData.selection
            return (
                <Box sx={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
                    {pluginData.help && <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} title={pluginData.label} content={pluginData.help} />}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FormControl fullWidth size="small" sx={{ my: 1 }} disabled={isDisabled}>
                            <InputLabel>{t(pluginData.label)}</InputLabel>
                            <Select value={currentValue ?? ((typeof selection?.[0] === 'object') ? selection[0].value : 0)} label={pluginData.label} onChange={(e) => handleChange(e.target.value)}>
                                {selection.map((e, i) => {
                                    const isObj = typeof e === 'object' && e !== null;
                                    const val = isObj ? e.value : i;
                                    const lab = isObj ? e.label : e;
                                    return <MenuItem value={val} key={val}>{t(lab)}</MenuItem>
                                })}
                            </Select>
                        </FormControl>
                        {pluginData.help && (
                            <Button size="small" startIcon={<HelpOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setHelpOpen(true)}
                                sx={{ color: '#f44336', fontSize: '0.72rem', px: 1, py: 0.3, minWidth: 0,
                                    border: '1px solid #f44336', borderRadius: 1, textTransform: 'none', fontWeight: 600, flexShrink: 0,
                                    '&:hover': { color: '#fff', bgcolor: 'rgba(244,67,54,0.15)' } }}>
                                Yardım
                            </Button>
                        )}
                    </Box>
                    <HintText />
                </Box>
            )
        }
        case "Slider":
            return (
                <Box sx={{ opacity: isDisabled ? 0.4 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}>
                    <Box sx={{ px: 2, my: 2 }}>
                        <Typography variant="caption" color="gray">{t(pluginData.label)} ({currentValue}%)</Typography>
                        <Slider size="small" value={currentValue} onChange={(_, nv) => handleChange(nv)} disabled={isDisabled} />
                    </Box>
                    <HintText />
                </Box>
            )
        case "MultiSelect":
            const selectedIds = Array.isArray(currentValue) ? currentValue : [];
            return (
                <Box>
                    <Box sx={{ my: 1, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1, p: 1.5, bgcolor: 'rgba(0,0,0,0.2)' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#90caf9', fontWeight: 'bold' }}>{t(pluginData.label)}</Typography>
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {pluginData.selection.map((item) => {
                                const isObj = typeof item === 'object' && item !== null;
                                const unitId = isObj ? item.value : item;
                                const unitLabel = isObj ? item.label : item;
                                const isChecked = selectedIds.includes(unitId);
                                return (
                                    <FormControlLabel
                                        key={unitId}
                                        control={<Checkbox size="small" checked={isChecked} onChange={(e) => {
                                            const newSelection = e.target.checked ? [...selectedIds, unitId] : selectedIds.filter(id => id !== unitId);
                                            handleChange(newSelection);
                                        }} />}
                                        label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{unitLabel}</Typography>}
                                        sx={{ display: 'block', my: 0.3 }}
                                    />
                                );
                            })}
                        </Box>
                        <Typography variant="caption" color="gray" sx={{ mt: 1, display: 'block' }}>{selectedIds.length} birim seçildi</Typography>
                    </Box>
                    <HintText />
                </Box>
            )
        case "Table":
            return (
                <Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'rgba(0,0,0,0.2)', my: 1 }}>
                        <Table size="small">
                            <TableHead><TableRow>
                                {pluginData.row.map(c => <TableCell key={c} sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>{t(c)}</TableCell>)}
                            </TableRow></TableHead>
                            <TableBody>
                                {array_chunks(pluginData.data, pluginData.row.length).map((chunk, idx) => (
                                    <TableRow key={idx}>
                                        {chunk.map(item => (
                                            <TableCell key={item.key} sx={{ p: 0.5 }}>
                                                <PluginOption pluginData={item} parentKey={parentKey} userPlugins={userPlugins} onChange={onChange} t={t} presetNames={presetNames} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <HintText />
                </Box>
            )
        default: return null
    }
}

export default function PluginsTable(props) {
    const { language, singlePlugin, presetNames } = props;
    const t = (key) => getTranslation(language, key);

    const userPlugins = props.userPlugins ?? {}

    // Single plugin view (Sidebar'dan seçilen)
    if (singlePlugin) {
        const optionGroups = []
        let activeSection = null
        let standaloneFields = []

        for (const option of singlePlugin.pluginOptions ?? []) {
            if (option.type === 'SettingsSection') {
                if (standaloneFields.length > 0) {
                    optionGroups.push({ fields: standaloneFields })
                    standaloneFields = []
                }
                activeSection = { ...option, fields: [] }
                optionGroups.push(activeSection)
            } else if (activeSection) {
                activeSection.fields.push(option)
            } else {
                standaloneFields.push(option)
            }
        }

        if (standaloneFields.length > 0) optionGroups.push({ fields: standaloneFields })

        const renderOptionGrid = (options) => (
            <Grid container spacing={2}>
                {options.map((opt) => (
                    <Grid item xs={opt.type === 'Table' || opt.type === 'Label' || opt.type === 'TextArea' || opt.type === 'MultiSelect' || opt.type === 'FieldGroup' || opt.width === 12 ? 12 : 6} key={opt.key || opt.label || opt.id}>
                        <PluginOption pluginData={opt} parentKey={singlePlugin.key} userPlugins={userPlugins} onChange={props.onChange} t={t} presetNames={presetNames} />
                    </Grid>
                ))}
            </Grid>
        )

        return (
            <Box>
                {optionGroups.map((group, index) => group.type === 'SettingsSection' ? (
                    <Box key={group.id || group.label} sx={{ mt: 3, p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid rgba(144,202,249,0.28)', bgcolor: 'rgba(17, 38, 60, 0.42)' }}>
                        <Typography variant="subtitle1" sx={{ color: '#90caf9', fontWeight: 'bold', mb: group.description ? 0.5 : 1.5 }}>{t(group.label)}</Typography>
                        {group.description && <Typography variant="body2" sx={{ color: '#92a4b5', mb: 2, lineHeight: 1.55 }}>{t(group.description)}</Typography>}
                        {renderOptionGrid(group.fields)}
                    </Box>
                ) : (
                    <React.Fragment key={`standalone-${index}`}>{renderOptionGrid(group.fields)}</React.Fragment>
                ))}
            </Box>
        )
    }

    // Default view (Fallback - List view)
    return (
        <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a' }}>
            <Table size="small">
                <TableHead><TableRow>
                    <TableCell sx={{ color: '#90caf9' }}>{t("Name")}</TableCell>
                    <TableCell sx={{ color: '#90caf9' }}>{t("Description")}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                    {props.plugins.filter(p => p.key !== 'presets').map(p => (
                        <TableRow key={p.key}>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t(p.name)}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{t(p.description)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
